import axios from 'axios';
import { apiOrigin } from './env';

// isIPHost reports whether a hostname is a bare IP address rather than a name.
// IPv6 arrives from window.location.hostname wrapped in brackets.
const isIPHost = (host: string): boolean =>
  /^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.startsWith('[') || host.includes(':');

// getSubdomain derives the tenant from the hostname's first label
// (e.g. "jabez" from jabez.datasirpi.dev). No env fallback — the tenant always
// comes from the URL.
//
// In local dev a tenant subdomain of localhost carries it the same way
// (e.g. "jabez" from jabez.localhost).
//
// Returns "" when the URL cannot carry one: a bare IP, bare localhost, or a
// single-label host. Splitting an IP on "." would otherwise yield "54" from
// 54.146.155.39 and send that as the tenant — a label that resolves nowhere and
// blocks the app behind "no workspace configured". An on-prem server is reached
// by IP long before it has DNS, so the backend answers the empty case with the
// only tenant it has.
export const getSubdomain = (): string => {
  const host = (window?.location?.hostname || '').toLowerCase();
  if (!host || host === 'localhost') return '';
  if (host.endsWith('.localhost')) return host.slice(0, -'.localhost'.length).split('.')[0] || '';
  if (isIPHost(host)) return '';
  const labels = host.split('.');
  if (labels.length < 2) return '';
  return labels[0] || '';
};

// Module-level current tenant, set once resolved. Read by the api.ts request
// interceptors to attach the X-Tenant-Id header to every API call.
let currentTenant: string | null = null;

export const getTenant = (): string | null => currentTenant;
export const setTenant = (t: string | null): void => {
  currentTenant = t;
};

export interface TenantCheckResult {
  available: boolean;
  subdomain: string;
  client_id?: string | null;
}

// checkTenant asks the backend whether a tenant exists for the given subdomain.
// Uses a bare axios call (no auth/tenant headers) since it runs before the tenant
// is known.
export const checkTenant = async (subdomain: string): Promise<TenantCheckResult> => {
  const { data } = await axios.get<TenantCheckResult>(`${apiOrigin()}/api/v1/tenants/check`, {
    params: { subdomain },
    timeout: 10_000,
  });
  return data;
};

// initTenant resolves the tenant from the URL and verifies it exists. On success
// the tenant is stored so subsequent API calls carry the X-Tenant-Id header.
export const initTenant = async (): Promise<TenantCheckResult> => {
  const subdomain = getSubdomain();
  const result = await checkTenant(subdomain);
  // Prefer the subdomain the backend reports over the one derived from the URL.
  // On a single-tenant install reached by IP there was nothing to derive, and
  // the answer is how the SPA learns which tenant to name on every later call.
  setTenant(result.available ? result.subdomain || subdomain || null : null);
  return result;
};
